import { Button } from '@/app/common/components/button/Button'
import DottedCircle from '@/app/common/components/DottedCircle'
import IconBox from '@/app/common/components/IconBox'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { CheckMarkIcon } from '@/app/common/icons/arrows'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useGeneratePreviewToken } from '../configureStore/hooks/useGeneratePreviewToken'
import { MARKETPLACE_STATUS_MAP } from '../ProductForm/constants'

type Props = {
  open: boolean
  marketplaceStatus: string
  onOkClick: () => void
  programId: number
  providerId: number
}

export default function EditProductSuccessAlert({
  open,
  programId,
  providerId,
  onOkClick,
  marketplaceStatus,
}: Props) {
  const previewTokenMutation = useGeneratePreviewToken()

  function getContent(marketplaceStatus: string) {
    if (marketplaceStatus === MARKETPLACE_STATUS_MAP.IN_PREVIEW) {
      return (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <IconBox className="flex-shrink-0">
              <DottedCircle state="active" />
            </IconBox>

            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-medium">Your Final Review</div>
                <span className="ml-auto inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-700">
                  In&nbsp;Progress
                </span>
              </div>
              <div className="text-sm text-gray-600">
                You'll review and approve the changes before going live.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <IconBox className="flex-shrink-0">
              <DottedCircle state="active" />
            </IconBox>

            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-medium">Go Live!</div>
              </div>
              <div className="text-sm text-gray-600">
                Your product will be published and available to customers.
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (marketplaceStatus === MARKETPLACE_STATUS_MAP.ACTIVE) {
      return (
        <div className="flex items-start gap-3">
          <IconBox className="flex-shrink-0">
            <CheckMarkIcon className="text-green-600" />
          </IconBox>

          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium">Go Live!</div>
            </div>
            <div className="text-sm text-gray-600">
              Your product is published and available to customers right now.
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="flex items-start gap-3">
        <IconBox className="flex-shrink-0">
          <DottedCircle state="active" />
        </IconBox>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-medium">Product is Inactive</div>
          </div>
          <div className="text-sm text-gray-600">
            Your product is inactive and won’t show up in the store preview or
            the live store.
          </div>
        </div>
      </div>
    )
  }

  const isInPreview = marketplaceStatus === MARKETPLACE_STATUS_MAP.IN_PREVIEW

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent showClose={false} className="sm:max-w-[520px] p-0">
        <div className="p-5">
          <DialogHeader className="mb-2">
            <DialogTitle className="text-xl">
              Product added Successfully
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-gray-600">
            We've received your product and it's now being reviewed. Here's what
            happens next:
          </p>

          <div className="mt-4">{getContent(marketplaceStatus)}</div>

          {isInPreview ? (
            <div className="mt-10 space-y-2">
              <SubmitButton
                type="button"
                className="w-full"
                size={'lg'}
                onClick={() => {
                  previewTokenMutation.openMarketplace({
                    programId,
                    providerId: providerId.toString(),
                  })
                }}
                buttonTxt="Preview Changes"
                status={previewTokenMutation.status}
              />
              <Button
                variant={'outline'}
                className="w-full"
                onClick={() => {
                  onOkClick()
                }}
              >
                Close
              </Button>
            </div>
          ) : (
            <div className="mt-10">
              <Button
                disabled={isInPreview}
                className="w-full"
                size={'lg'}
                onClick={() => {
                  onOkClick()
                }}
              >
                Okay
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
